import { inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { IsLoggedInGuard, RequiresPermissionGuard } from '@lexplus-authentication';
import { LexplusPermissionsEnum } from '@lexplus-authentication-api';
import { DetailDrawerType } from '@lexplus-shared-lib';
import { LOCALIZED_USER_ROUTES, USER_ROUTES_IDS } from '../../../localized-user-routes-const';
import { UserDetailDrawerTypeKey } from '../../../global/models/user-detail-drawer-type-key.model';

/** Both Compliance permissions open the overview, its requirements and its Erlasse; the host page guards the same. */
export const COMPLIANCE_PERMISSIONS: LexplusPermissionsEnum[] = [
  LexplusPermissionsEnum.UserComplianceuebersichtAlle,
  LexplusPermissionsEnum.UserComplianceuebersichtEigene,
];

/** The route data flag the archived kind sets, which switches the shared page to its read-only archive. */
const ARCHIVED_ANFORDERUNG_DATA_KEY = 'archived';

/**
 * An Anforderung as the detail drawer shows it, behind the same permissions as the Compliance Übersicht.
 *
 * @param {string} locale - The base language, which localizes the segment
 * @returns {DetailDrawerType} The Anforderung's definition for the drawer
 */
export function buildAnforderungDetailDrawerType(locale: string): DetailDrawerType<UserDetailDrawerTypeKey> {
  return {
    segment: LOCALIZED_USER_ROUTES[USER_ROUTES_IDS.ANFORDERUNGEN][locale],
    label: $localize`:@@compliance.drawer.typ:Anforderung`,
    drawerLabel: $localize`:@@compliance.drawer.label:Anforderung-Details`,
    loadComponent: () =>
      import('../pages/anforderung-detail-page/anforderung-detail-page.component').then((m) => m.AnforderungDetailPageComponent),
    canActivate: [IsLoggedInGuard, RequiresPermissionGuard],
    data: { requiredAnyPermission: COMPLIANCE_PERMISSIONS },
    contains: ['aufgabe'],
    stacks: ['dokument'],
    opens: ['erlass'],
  };
}

/**
 * An archived Anforderung, on the same page in its read-only mode. A kind of its own, because the
 * version ID cannot tell the two apart: a fifth of the archived versions are still in force at
 * another Standort. It links to the active requirement and to its Erlass, each a first level.
 *
 * @param {string} locale - The base language, which localizes the segment
 * @returns {DetailDrawerType} The archived Anforderung's definition for the drawer
 */
export function buildArchivierteAnforderungDetailDrawerType(locale: string): DetailDrawerType<UserDetailDrawerTypeKey> {
  const anforderungType = buildAnforderungDetailDrawerType(locale);

  return {
    ...anforderungType,
    segment: LOCALIZED_USER_ROUTES[USER_ROUTES_IDS.ARCHIVIERTE_ANFORDERUNGEN][locale],
    drawerLabel: $localize`:@@compliance.archiv.drawer.label:Details der archivierten Anforderung`,
    data: { ...anforderungType.data, [ARCHIVED_ANFORDERUNG_DATA_KEY]: true },
    opens: ['anforderung', 'erlass'],
  };
}

/**
 * Must run in an injection context below the drawer level, such as a provider factory of the page.
 *
 * @returns {boolean} Whether the page stands on the archived kind rather than on an Anforderung in force
 */
export function injectIsArchivedAnforderung(): boolean {
  return inject(ActivatedRoute).snapshot.data[ARCHIVED_ANFORDERUNG_DATA_KEY] === true;
}
